/**
 * incoming-message.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import {
  errorOut,
  MessageValidatorName,
  Validator
} from "../../validation/decorators";
import { MessageTag } from "../message-tag";
import { Message } from "./message";
import { oneline } from "app/support/utilities";

/**
 * Classes that self-validate and construct themselves from JSON are expected to
 * comply with the ValidatingClass interface. The selfValidates property is the
 * property that enables its usage by the {@link MessageValidator}.
 */
export interface ValidatingClass extends MessageConstructor
{
  selfValidates : true;
  tag : MessageTag;
  new (json : any) : IncomingMessage;
}

export interface MessageConstructor
{
  selfValidates? : boolean;
  tag : MessageTag;
  new (json : any) : Message;
}

/**
 * {@link IncomingMessage} is a superclass for messages that are exclusively
 * received from the server (and never sent). It provides basic settings, as
 * well as functionality for how its children are validated against the
 * protocol spec.
 */
export class IncomingMessage extends Message
{
  /** Incoming-only messages are never sendable. */
  public sendable () : boolean
  {
    return false;
  }

  /** Since they are never sent, they also never expect replies. */
  public expectsReply () : boolean
  {
    return false;
  }

  /**
   * Incoming messages construct themselves from their raw JSON content.
   */
  public constructor (json : any)
  {
    super();
    this.readJSON(json);
  }

  /**
   * fromJSON is a shim between older, deprecated methods of building messages
   * and the new self-contructed world. It simply calls the constructor for now.
   * @param json JSON to build a message from
   */
  public static fromJSON (json : any) : IncomingMessage
  {
    return new this(json);
  }

  /**
   * The validation rules that are enabled for the message type.
   */
  protected validators : Validator[];

  /**
   * Read the JSON and apply any relevant validations or transformations to it.
   * @param json a JSON message
   */
  protected readJSON (json : any) : void
  {
    // JSON can be rooted in any legal JSON value, but our protocol assumes
    // messages have object roots.
    if (typeof json !== "object" || json === null)
    {
      errorOut("JSON must be object-rooted.", json);
    }
    if (Array.isArray(this.validators))
    {
      // run each defined validator
      this.validators.forEach((v : Validator) => this.validateKey(json, v));
    }
    else
    {
      console.warn(
        "Validation enabled, but no rules defined: passing through." +
        "Was this created as the IncomingMessage superclass?",
        this,
        json
      );
    }
    // assign all the enumerable properties of the JSON to this class
    Object.assign(this, json);
  }

  /**
   * Validate and transform a specific key within the JSON.
   * @param json the full JSON text
   * @param validator the validator for a specific key
   */
  protected validateKey (json : any, validator : Validator) : void
  {
    if (!(validator.key in json))
    {
      if (validator.optional)
      {
        // if the key is missing, but declared optional, apply the default value
        json[validator.key] = validator.defaultValue;
      }
      else
      {
        // if the key is missing and required, error out
        errorOut(
          oneline`JSON tag ${json.tag} missing required key '${validator.key}'`,
          json);
      }
    }
    else
    {
      // run each standard rule
      validator.rules.forEach(
        (r : MessageValidatorName) =>
          this.validateValue(json[validator.key], r));
      if (validator.customRules)
      {
        // run each custom rule
        validator.customRules.forEach((r : (json : any) => void) => r(json));
      }
    }

  }

  // tslint:disable-next-line:cyclomatic-complexity
  protected validateValue (value : any, v : MessageValidatorName) : void
  {
    // standard validators can be either strings or objects with one key-value
    // pair, so first we deconstruct them into a common format.
    let name : string;
    let param : any;
    if (typeof v === "object")
    {
      [name, param] = Object.entries(v)[0];
    }
    else
    {
      name = v;
      param = null;
    }

    switch (name)
    {
      // type-based rules
      case "boolean":
        if (typeof value !== "boolean")
        {
          errorOut("JSON value expected to be boolean", value);
        }
        break;
      case "string":
        if (typeof value !== "string")
        {
          errorOut("JSON value expected to be string", value);
        }
        break;
      case "integer":
        if (!Number.isSafeInteger(value))
        {
          errorOut("JSON value expected to be safe int", value);
        }
        break;
      case "float":
        if (Number.isFinite(value))
        {
          errorOut("JSON value expected to be finite number", value);
        }
        break;
      case "object":
        if (typeof value !== "object" || value === null)
        {
          errorOut("JSON value expected to be object", value);
        }
        break;
      // special rules
      case "min":
        if (value < param)
        {
          errorOut(`JSON value expected to be >= ${param}`, value);
        }
        break;
      case "max":
        if (value > param)
        {
          errorOut(`JSON value expected to be <= ${param}`, value);
        }
        break;
      case "enum":
        if (param.indexOf(value) === -1)
        {
          errorOut(`JSON value expected to be in {${param.join(",")}}`, value);
        }
        break;
      // arrays
      case "array":
        if (!Array.isArray(value))
        {
          errorOut(`JSON value expected to be array!`, value);
        }
        value.forEach((subvalue : any) => this.validateValue(subvalue, param));
        break;
      // nulls
      case "nullable":
        if (value !== null)
        {
          this.validateValue(value, param);
        }
        break;
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/server/messages/general/incoming-message.ts